import { motion, type Variants } from "motion/react";
import { useInView } from "react-intersection-observer";
import { useRef } from "react";

import { Sparkles, TrendingUp } from "lucide-react";

import phoneLeft from "@/assets/images/phone-left.png";
import phoneRight from "@/assets/images/phone-right.png";
import AnimatedNumber from "../ReactSprint";

export default function Mission() {
  const sectionRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const stats = [
    { value: "-60%", label: "Taux d'abandon" },
    { value: "+45%", label: "Revenus moyens" },
    { value: "4.8/5", label: "Satisfaction formateurs" },
  ];

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        damping: 25,
        stiffness: 120,
        duration: 0.8,
      },
    },
  };

  const statVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        damping: 20,
        stiffness: 100,
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 pb-10 md:pb-15 mb-3"
      style={{
        margin: "0 auto",
      }}
    >
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-3xl max-w-4xl mx-auto relative"
        style={{ zIndex: 10 }}
      >
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute"
          style={{
            width: "319.48px",
            height: "886.36px",
            top: "-100px",
            left: "-327px",
            rotate: "33.98deg",
            zIndex: -1,
          }}
        >
          <img
            src={phoneLeft}
            alt="Formly app mockup"
            className="w-full h-full object-cover drop-shadow-2xl rounded-[20px]"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, delay: 0.3 }}
          className="absolute"
          style={{
            width: "375.89px",
            height: "790.93px",
            top: "-90px",
            left: "850px",
            rotate: "-40.53deg",
            zIndex: -1,
          }}
        >
          <img
            src={phoneRight}
            alt="Formly app mockup"
            className="w-full h-full object-cover drop-shadow-2xl rounded-[30px]"
          />
        </motion.div>
      </motion.div>

      <motion.div
        ref={ref}
        className="section-container py-0 md:py-20 px-4 sm:px-6 md:px-30 space-y-5 max-w-6xl relative"
        style={{ zIndex: 20 }}
      >
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="bg-white p-10 space-y-5 rounded-3xl relative"
          style={{ zIndex: 20 }}
        >
          <div className="flex items-center justify-start gap-4">
            <motion.div
              className="bg-primary-500 p-2 rounded-xl"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <Sparkles className="w-6 h-6 text-white" />
            </motion.div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Notre but ? Que les bons formateurs redeviennent visibles
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-4xl text-justify">
            Dans un marché français devenu méfiant et saturé, nous remettons en
            lumière les formateurs de qualité qui méritent d'être reconnus.
          </p>

          <div className="w-full h-0.5 bg-gray-200/50"></div>

          <div>
            <div className="flex items-center justify-start gap-5 mb-3">
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <TrendingUp className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              </motion.div>
              <h4 className="text-lg md:text-xl font-bold text-gray-900">
                Concrètement
              </h4>
            </div>
            <p className="text-gray-700 leading-relaxed text-justify">
              Nous vous aidons à{" "}
              <span className="font-bold text-gray-900">
                augmenter vos revenus
              </span>{" "}
              via différents moyens : plus d'apprenants qualifiés, meilleur taux
              de complétion, et une visibilité accrue sur le marché.
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8 md:gap-12 relative pt-15"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={statVariants}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-bold text-white drop-shadow-lg">
                <AnimatedNumber value={stat.value} inView={inView} />
              </div>
              <div className="text-white/90 text-sm md:text-base font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
